import { PageViewEvent } from '@shopify/events';
import '@theme/view-event-elements';

const FOCUSABLE = 'a[href], button, input, summary';
const LOOKAHEAD = '0px 0px 600px 0px';
const SECTIONS_PER_REQUEST = 5;

const scrollRoot = (el) => {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
};

const scrollTopOf = (scroller) => (scroller ? scroller.scrollTop : window.scrollY);

const templateSectionIds = () =>
  [...document.querySelectorAll('.shopify-section[id^="shopify-section-template--"]')].map((el) =>
    el.id.replace('shopify-section-', '')
  );

const shownSections = new Map();

const fetchSections = async (url, ids, signal) => {
  const batches = [];
  for (let i = 0; i < ids.length; i += SECTIONS_PER_REQUEST) batches.push(ids.slice(i, i + SECTIONS_PER_REQUEST));
  const results = await Promise.all(
    batches.map(async (batch) => {
      const request = new URL(url);
      request.searchParams.set('sections', batch.join(','));
      const response = await fetch(request, { signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      return response.json();
    })
  );
  return Object.assign({}, ...results);
};

const keepStyles = (roots) => {
  const present = new Set([...document.head.querySelectorAll('link[rel="stylesheet"]')].map((link) => link.href));
  const loads = [];
  for (const root of roots) {
    for (const link of root.querySelectorAll('link[rel="stylesheet"]')) {
      if (!present.has(link.href)) {
        present.add(link.href);
        const copy = document.createElement('link');
        for (const { name, value } of link.attributes) copy.setAttribute(name, value);
        loads.push(
          new Promise((resolve) => {
            copy.addEventListener('load', resolve, { once: true });
            copy.addEventListener('error', resolve, { once: true });
          })
        );
        document.head.append(copy);
      }
      if (!root.isConnected) link.remove();
    }
  }
  return Promise.all(loads);
};

const loadedScripts = new Set();

const loadScripts = (root) => {
  for (const el of document.scripts) if (el.src && !root.contains(el)) loadedScripts.add(el.src);
  for (const inert of root.querySelectorAll('script[src]')) {
    if (loadedScripts.has(inert.src)) continue;
    loadedScripts.add(inert.src);
    const script = document.createElement('script');
    for (const { name, value } of inert.attributes) script.setAttribute(name, value);
    document.head.append(script);
  }
};

const withoutPaging = (href) => {
  const url = new URL(href, window.location.origin);
  url.searchParams.delete('page');
  url.searchParams.delete('section_id');
  return url;
};

class DrvCollection extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.sectionId = this.dataset.sectionId;
    this.drawer = this.querySelector('[data-coll-drawer]');
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onPop = this.onPop.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);
    window.addEventListener('popstate', this.onPop);
    this.drawer?.addEventListener('close', () => this.querySelector('[data-coll-open]')?.focus());
    history.replaceState({ ...(history.state || {}), drvCollection: this.sectionId }, '');
    this.watchMore();
    this.watchBar();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
    window.removeEventListener('popstate', this.onPop);
    this.observer?.disconnect();
    this.barObserver?.disconnect();
    this.barRow = null;
    this.controller?.abort();
    this.moreController?.abort();
    this.started = false;
  }

  onClick(event) {
    if (event.target === this.drawer) {
      this.drawer.close();
      return;
    }
    if (event.target.closest('[data-coll-open]')) {
      this.drawer?.showModal();
      return;
    }
    if (event.target.closest('[data-coll-close]')) {
      this.drawer?.close();
      return;
    }
    const arrow = event.target.closest('[data-coll-scroll]');
    if (arrow) {
      this.scrollBar(Number(arrow.dataset.collScroll));
      return;
    }
    const target = event.target.closest('a[data-coll-switch]');
    if (target && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      this.switchTo(target.href);
      return;
    }
    const link = event.target.closest('a[data-coll-link], a[data-coll-more]');
    if (!link) return;
    if (!link.hasAttribute('href')) {
      event.preventDefault();
      return;
    }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (link.hasAttribute('data-coll-more')) {
      this.loadMore(link, { auto: false });
    } else {
      this.closeSort();
      this.update(link.href);
    }
  }

  onChange(event) {
    const form = event.target.closest('[data-coll-form]');
    if (!form) return;
    const url = new URL(form.action, window.location.origin);
    url.search = new URLSearchParams(new FormData(form)).toString();
    this.update(url.href);
  }

  onSubmit(event) {
    if (!event.target.closest('[data-coll-form]')) return;
    event.preventDefault();
    this.drawer?.close();
  }

  onDocClick(event) {
    const sort = this.querySelector('[data-coll-sort][open]');
    if (sort && !sort.contains(event.target)) sort.open = false;
  }

  onKey(event) {
    if (event.key !== 'Escape') return;
    const sort = this.querySelector('[data-coll-sort][open]');
    if (!sort) return;
    sort.open = false;
    sort.querySelector('summary')?.focus();
  }

  onPop(event) {
    if (event.state?.drvCollection !== this.sectionId) return;
    if (window.location.pathname === this.dataset.collectionPath) {
      this.update(window.location.href, { push: false });
      return;
    }
    this.switchTo(window.location.href, { push: false, scrollBefore: scrollTopOf(scrollRoot(this)) });
  }

  closeSort() {
    const sort = this.querySelector('[data-coll-sort][open]');
    if (sort) sort.open = false;
  }

  async fetchSection(url, signal) {
    const request = new URL(url, window.location.origin);
    request.searchParams.set('section_id', this.sectionId);
    const response = await fetch(request, { signal });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  async update(href, { push = true } = {}) {
    const url = withoutPaging(href);
    this.controller?.abort();
    this.moreController?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.fetchSection(url, controller.signal);
      const fresh = doc.querySelector('drv-collection');
      if (!fresh) throw new Error('Section markup missing');
      this.swap(fresh);
      if (push) history.pushState({ drvCollection: this.sectionId }, '', url.pathname + url.search);
      this.announce(this.querySelector('.drv-coll__count')?.textContent);
      this.watchMore();
      this.watchBar();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(url.href);
    } finally {
      if (this.controller === controller) this.removeAttribute('aria-busy');
    }
  }

  async switchTo(href, { push = true, scrollBefore = null } = {}) {
    const url = withoutPaging(href);
    this.controller?.abort();
    this.moreController?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setAttribute('aria-busy', 'true');
    try {
      const ids = templateSectionIds();
      const unknown = ids.filter((id) => !shownSections.has(id));
      const [html, shown] = await Promise.all([
        fetchSections(url, ids, controller.signal),
        fetchSections(new URL(window.location.href), unknown, controller.signal),
      ]);
      if (!html[this.sectionId]) throw new Error('Collection section missing');
      for (const [id, markup] of Object.entries(shown)) if (typeof markup === 'string') shownSections.set(id, markup);

      const changes = [];
      for (const id of ids) {
        const current = document.getElementById(`shopify-section-${id}`);
        if (!current || typeof html[id] !== 'string') continue;
        if (id !== this.sectionId && html[id] === shownSections.get(id)) continue;
        const next = new DOMParser().parseFromString(html[id], 'text/html').getElementById(`shopify-section-${id}`);
        if (next) changes.push({ id, current, next });
      }
      await keepStyles(changes.flatMap(({ current, next }) => [current, next]));
      if (controller.signal.aborted) return;

      const scroller = scrollRoot(this);
      const anchorRoot = scroller || document.scrollingElement;
      anchorRoot.style.overflowAnchor = 'none';
      const top = this.getBoundingClientRect().top;
      const barFocused = this.querySelector('[data-coll-part="bar"]')?.contains(document.activeElement);
      for (const { id, current, next } of changes) {
        current.replaceWith(next);
        shownSections.set(id, html[id]);
        loadScripts(next);
      }

      if (push) history.pushState({ drvCollection: this.sectionId }, '', url.pathname + url.search);
      const fresh = document.querySelector(`drv-collection[data-section-id="${this.sectionId}"]`);
      if (fresh) {
        if (fresh.dataset.pageTitle) document.title = fresh.dataset.pageTitle;
        const shift = fresh.getBoundingClientRect().top - top;
        const restored = scrollBefore !== null && Math.abs(scrollTopOf(scroller) - scrollBefore) > 1;
        if (shift && !restored) (scroller || window).scrollBy({ top: shift, behavior: 'instant' });
        if (barFocused) fresh.querySelector('.drv-coll__pill.is-active')?.focus({ preventScroll: true });
        fresh.announce?.(fresh.querySelector('.drv-coll__count')?.textContent);
      }
      requestAnimationFrame(() => anchorRoot.style.removeProperty('overflow-anchor'));
      const template = document.querySelector('main[data-template]')?.dataset.template || '';
      document.dispatchEvent(new PageViewEvent({ page: { template, title: document.title, url: window.location.href } }));
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(url.href);
    } finally {
      if (this.controller === controller) this.removeAttribute('aria-busy');
    }
  }

  swap(fresh) {
    const active = document.activeElement;
    const activePart = active && this.contains(active) ? active.closest('[data-coll-part]') : null;
    const activeIndex = activePart ? [...activePart.querySelectorAll(FOCUSABLE)].indexOf(active) : -1;
    const activeName = activePart?.dataset.collPart;
    const activeInput = active?.matches?.('input[name]') ? { name: active.name, value: active.value } : null;

    for (const part of this.querySelectorAll('[data-coll-part]')) {
      const next = fresh.querySelector(`[data-coll-part="${part.dataset.collPart}"]`);
      if (!next) continue;
      if (part.dataset.collPart === 'bar' && (part.isEqualNode(next) || this.mergeBar(part, next))) continue;
      part.replaceWith(next);
    }

    if (!activeName || this.contains(active)) return;
    const part = this.querySelector(`[data-coll-part="${activeName}"]`);
    const focusable = part ? [...part.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled) : [];
    const sameInput = activeInput && focusable.find((el) => el.name === activeInput.name && el.value === activeInput.value);
    const target = sameInput || focusable[Math.min(activeIndex, focusable.length - 1)];
    if (target) target.focus({ preventScroll: true });
    else if (this.drawer?.open) this.drawer.querySelector('[data-coll-close]')?.focus();
    else this.querySelector('[data-coll-grid]')?.focus({ preventScroll: true });
  }

  mergeBar(part, next) {
    const pills = [...part.querySelectorAll('[data-coll-value]')];
    const incoming = new Map([...next.querySelectorAll('[data-coll-value]')].map((pill) => [pill.dataset.collValue, pill]));
    if (!pills.length || [...incoming.keys()].some((key) => !pills.some((pill) => pill.dataset.collValue === key))) return false;
    for (const pill of pills) {
      const match = incoming.get(pill.dataset.collValue);
      const isActive = Boolean(match?.classList.contains('is-active'));
      pill.classList.toggle('is-active', isActive);
      pill.toggleAttribute('aria-current', isActive);
      if (isActive) pill.setAttribute('aria-current', 'true');
      if (match) {
        pill.setAttribute('href', match.getAttribute('href'));
        pill.removeAttribute('aria-disabled');
      } else {
        pill.removeAttribute('href');
        pill.setAttribute('aria-disabled', 'true');
      }
    }
    return true;
  }

  watchBar() {
    const row = this.querySelector('[data-coll-pills]');
    if (row !== this.barRow) {
      this.barObserver?.disconnect();
      this.barRow = row;
      if (row) {
        row.addEventListener('scroll', () => this.updateBar(), { passive: true });
        this.barObserver = new ResizeObserver(() => this.updateBar());
        this.barObserver.observe(row);
        document.fonts?.ready.then(() => {
          this.revealActivePill();
          this.updateBar();
        });
        this.revealActivePill();
      }
    }
    this.updateBar();
  }

  revealActivePill() {
    const row = this.barRow;
    const pill = row?.querySelector('.drv-coll__pill.is-active');
    if (!pill) return;
    const rowBox = row.getBoundingClientRect();
    const box = pill.getBoundingClientRect();
    if (box.left >= rowBox.left && box.right <= rowBox.right) return;
    row.scrollBy({ left: box.left - rowBox.left - (rowBox.width - box.width) / 2, behavior: 'instant' });
  }

  updateBar() {
    const row = this.barRow;
    const overflow = row ? row.scrollWidth - row.clientWidth : 0;
    const position = row ? Math.abs(row.scrollLeft) : 0;
    this.toggleAttribute('data-bar-start', overflow > 1 && position > 1);
    this.toggleAttribute('data-bar-end', overflow > 1 && position < overflow - 1);
  }

  scrollBar(direction) {
    const row = this.barRow;
    if (!row) return;
    const flip = getComputedStyle(row).direction === 'rtl' ? -1 : 1;
    row.scrollBy({ left: direction * flip * row.clientWidth * 0.75, behavior: 'smooth' });
  }

  async loadMore(link, { auto }) {
    if (this.loading) return;
    this.loading = true;
    link.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    this.moreController = controller;
    try {
      const doc = await this.fetchSection(link.href, controller.signal);
      const grid = this.querySelector('[data-coll-grid]');
      const incoming = [...(doc.querySelector('[data-coll-grid]')?.children || [])];
      const firstCard = incoming.find((item) => item.classList.contains('drv-coll__item'));
      grid.append(...incoming);
      const wrap = doc.querySelector('[data-coll-more-wrap]');
      if (wrap) this.querySelector('[data-coll-more-wrap]')?.replaceWith(wrap);
      this.announce(this.dataset.loadedText);
      if (!auto) firstCard?.querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(link.href);
    } finally {
      link.removeAttribute('aria-busy');
      this.loading = false;
      this.watchMore();
    }
  }

  watchMore() {
    this.observer?.disconnect();
    if (!this.hasAttribute('data-auto-load')) return;
    const link = this.querySelector('[data-coll-more]');
    if (!link) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore(link, { auto: true });
      },
      { root: scrollRoot(this), rootMargin: LOOKAHEAD }
    );
    this.observer.observe(link);
  }

  announce(text) {
    const live = this.querySelector('[data-coll-live]');
    if (!live || !text) return;
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = text.trim();
    });
  }
}

if (!customElements.get('drv-collection')) {
  customElements.define('drv-collection', DrvCollection);
}
